import { create } from 'zustand';
import { createDebouncedJSONStorage } from 'zustand-debounce';
import { persist } from 'zustand/middleware';
import { LegendStore } from '../types/legend';
import indexedDbStorage from '../utils/indexeddb-zustand-store';

export const storageName = 'MOOD_legend_store' as const;

const useLegendStore = create<LegendStore>()(
  persist(
    (set) => ({
      legend: null,
      setLegend: (legend) => set({ legend: legend }),

      showSurfaceMap: true,
      setShowSurfaceMap: (state) => set({ showSurfaceMap: state }),

      showGlobalShapeFile: true,
      setShowGlobalShapeFile: (state) => set({ showGlobalShapeFile: state }),

      showMesh: true,
      setShowMesh: (state) => set({ showMesh: state }),

      visibleUploads: [],
      setVisibleUploads: (uploads) => set({ visibleUploads: uploads }),

      uploadedFeatures: [],
      setUploadedFeatures: (features) => set({ uploadedFeatures: features }),

      uploadingShapefile: false,
      setUploadingShapefile: (state) => set({ uploadingShapefile: state }),

      showWavesZones: false,
      setShowWavesZones: (state) => set({ showWavesZones: state }),

      showOceanZones: false,
      setShowOceanZones: (state) => set({ showOceanZones: state }),

      showAtmosphereZones: false,
      setShowAtmosphereZones: (state) => set({ showAtmosphereZones: state }),

      showOcean3dZones: false,
      setShowOcean3dZones: (state) => set({ showOcean3dZones: state }),

      showLabelLayer: true,
      setShowLabelLayer: (state) => set({ showLabelLayer: state }),

      uploadedShapefile: [],
      setUploadedShapefile: (shapefile) => set({ uploadedShapefile: shapefile }),

      showInSituPoints: false,
      setShowInSituPoints: (state) => set({ showInSituPoints: state }),

      showFWEPoints: false,
      setShowFWEPoints: (state) => set({ showFWEPoints: state }),

      showUploadedShapefile: false,
      setShowUploadedShapefile: (state) => set({ showUploadedShapefile: state }),

      showLayerView: false,
      setShowLayerView: (state) => set({ showLayerView: state }),

      isMeasureMode: false,
      setIsMeasureMode: () => set((state) => ({ isMeasureMode: !state.isMeasureMode })),

      reset: () =>
        set({
          legend: null,
          showSurfaceMap: true,
          showGlobalShapeFile: true,
          showMesh: true,
          visibleUploads: [],
          uploadedFeatures: [],
          uploadingShapefile: false,
          showWavesZones: false,
          showOceanZones: false,
          showAtmosphereZones: false,
          showOcean3dZones: false,
          showLabelLayer: true,
          uploadedShapefile: [],
          showInSituPoints: false,
          showFWEPoints: false,
          showUploadedShapefile: false,
          showLayerView: false,
        }),
    }),
    {
      name: storageName,
      storage: createDebouncedJSONStorage(indexedDbStorage, {
        debounceTime: 500,
      }),
      version: 1,
      partialize: (state) => ({
        legend: state.legend,
        showSurfaceMap: state.showSurfaceMap,
        showGlobalShapeFile: state.showGlobalShapeFile,
        showMesh: state.showMesh,
        visibleUploads: state.visibleUploads,
        uploadedFeatures: state.uploadedFeatures,
        showWavesZones: state.showWavesZones,
        showOceanZones: state.showOceanZones,
        showAtmosphereZones: state.showAtmosphereZones,
        showOcean3dZones: state.showOcean3dZones,
        showLabelLayer: state.showLabelLayer,
        uploadedShapefile: state.uploadedShapefile,
        showFWEPoints: state.showFWEPoints,
        showUploadedShapefile: state.showUploadedShapefile,
        showLayerView: state.showLayerView,
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<LegendStore>),
        showInSituPoints: false,
      }),
    }
  )
);

export const isStoreEmpty = (state: LegendStore) => {
  return (
    state.legend === null &&
    state.visibleUploads.length === 0 &&
    state.uploadedFeatures.length === 0 &&
    state.uploadedShapefile.length === 0
  );
};

export default useLegendStore;
